<template>
  <v-dialog v-model="internalShow" max-width="1100" scrollable @click:outside="closeModal">
    <v-card v-if="details" class="rounded-lg">
      <!-- Encabezado con Folio y Estatus Global -->
      <v-card-title class="primary white--text d-flex justify-space-between align-center py-3">
        <span class="headline font-weight-bold">
          <v-icon dark left>mdi-package-variant-closed</v-icon>
          Orden de Venta #{{ details.folio }}
        </span>
        <v-chip dark small :color="getOverallStatusColor(details.overall_delivery_status_id)" class="font-weight-bold">
          {{ details.overall_delivery_status_description }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Información General (Cabecera) -->
        <v-row class="mb-2">
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">FECHA COMPRA</div>
            <div class="body-1 text--primary font-weight-medium">{{ formatDate(details.purchase_date) }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">CLIENTE</div>
            <div class="body-1 text--primary font-weight-medium">{{ details.client_name }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">VENDEDOR</div>
            <div class="body-1 text--primary font-weight-medium">{{ details.employee_name }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">SUCURSAL ORIGEN</div>
            <div class="body-1 text--primary font-weight-medium">{{ details.origin_store_name }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Tarjetas de Resumen Logístico y Financiero -->
        <v-row class="mb-3">
          <v-col cols="12" sm="4">
            <v-card outlined class="pa-3 text-center fill-height background-light">
              <div class="caption grey--text font-weight-bold">RESUMEN ARTÍCULOS</div>
              <div class="d-flex justify-space-around align-center mt-2">
                <div>
                  <div class="caption grey--text">Comprados</div>
                  <div class="title font-weight-bold primary--text">{{ details.total_items_ordered }}</div>
                </div>
                <div>
                  <div class="caption grey--text">Entregados</div>
                  <div class="title font-weight-bold success--text">{{ details.total_items_delivered }}</div>
                </div>
                <div>
                  <div class="caption grey--text">Pendientes</div>
                  <div class="title font-weight-bold warning--text">{{ details.total_items_pending }}</div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="4">
            <v-card outlined class="pa-3 text-center fill-height background-light">
              <div class="caption grey--text font-weight-bold">TOTAL VENTA</div>
              <div class="display-1 font-weight-bold primary--text mt-1">
                {{ formatMoney(details.total_amount) }}
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="4">
            <v-card outlined class="pa-3 fill-height background-light">
              <div class="caption grey--text text-center font-weight-bold mb-1">MÉTODOS DE PAGO</div>
              <div v-for="(pay, idx) in details.payments" :key="idx" class="d-flex justify-space-between caption">
                <span>{{ pay.payment_method }}:</span>
                <span class="font-weight-bold">{{ formatMoney(pay.amount) }}</span>
              </div>
              <div v-if="!details.payments || details.payments.length === 0" class="caption text-center grey--text">
                Sin desglose
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Listado de Productos -->
        <div class="subtitle-1 font-weight-bold mb-2 primary--text">Desglose de Productos</div>
        <v-simple-table dense class="elevation-1 border-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Producto</th>
                <th class="text-center">Comprados</th>
                <th class="text-center">Entregados</th>
                <th class="text-center">Pendientes</th>
                <th class="text-right">$ Unid.</th>
                <th class="text-right">Total</th>
                <th class="text-center">Estatus</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in details.items" :key="item.product_id">
                <td class="font-weight-medium">{{ item.product_name }}</td>
                <td class="text-center font-weight-bold">{{ item.quantity_ordered }}</td>
                <td class="text-center success--text font-weight-bold">{{ item.quantity_delivered }}</td>
                <td class="text-center warning--text font-weight-bold">{{ item.quantity_pending }}</td>
                <td class="text-right">{{ formatMoney(item.unit_price) }}</td>
                <td class="text-right font-weight-bold">{{ formatMoney(item.total_price) }}</td>
                <td class="text-center">
                  <v-chip x-small dark :color="getItemStatusColor(item.delivery_status_id)">
                    {{ item.delivery_status_description }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="closeModal">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Estado de Carga / Error -->
    <v-card v-else class="pa-5 text-center">
      <v-progress-circular v-if="isLoading" indeterminate color="primary" size="50"></v-progress-circular>
      <div v-else class="text-subtitle-1 error--text">
        No se pudo cargar la información de la orden.
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2';
import ClientDeliveryService from '@/services/ClientDeliveryService';
import { getOverallStatusColor, getItemStatusColor } from '@/utils/Constants';

export default {
  name: 'DeliveryOrderDetailsModal',
  props: {
    value: { type: Boolean, default: false },
    folio: { type: [Number, String], default: null },
    storeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      details: null,
      isLoading: false,
    };
  },
  computed: {
    internalShow: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
  },
  watch: {
    value(val) {
      if (val && this.folio) {
        this.fetchDetails();
      }
    },
  },
  methods: {
    getOverallStatusColor,
    getItemStatusColor,
    async fetchDetails() {
      this.isLoading = true;
      this.details = null;
      try {
        const response = await ClientDeliveryService.getDeliveryOrderDetail(this.folio, this.storeId);
        if (response.status === 'success' || response.success) {
          this.details = response.data;
        }
      } catch (error) {
        console.error('Error al cargar detalle:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al obtener detalles de la Venta',
          text: error,
          confirmButtonColor: 'primary',
        });
      } finally {
        this.isLoading = false;
      }
    },
    closeModal() {
      this.internalShow = false;
    },
    formatMoney(val) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.background-light {
  background-color: #f8fafc;
}
.border-table {
  border: 1px solid #e2e8f0;
}
</style>
